import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import ApiHealthChip from "../components/ApiHealthChip";

interface LocationState {
  from?: { pathname: string };
}

export default function LoginPage() {
  const { session, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [agencyId, setAgencyId] = useState(
    () => localStorage.getItem("occ.last_agency_id") ?? ""
  );
  const [employeeId, setEmployeeId] = useState("");
  const [pin, setPin] = useState("");
  const [pinVisible, setPinVisible] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (session) {
    return <Navigate to="/map" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await api.login(agencyId.trim(), employeeId.trim(), pin);
      localStorage.setItem("occ.last_agency_id", agencyId.trim());
      signIn({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        expiresAt: Date.now() + res.expires_in * 1000,
        userId: res.user_id,
        agencyId: res.agency_id,
        role: res.role,
        employeeId: employeeId.trim(),
      });
      const target =
        (location.state as LocationState | null)?.from?.pathname ?? "/map";
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setPending(false);
    }
  }

  const agencySaved = !!localStorage.getItem("occ.last_agency_id");

  return (
    <div className="login-shell" data-driver-skin>
      {/* ── Left brand pane ── */}
      <aside className="login-brand-pane" aria-label="Branding">
        <svg className="login-route-lines" viewBox="0 0 600 900" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {/* Route paths */}
          <path id="route1" d="M-40 380 Q100 420 280 400 Q460 380 560 340 Q650 310 720 380" stroke="rgba(196,168,100,0.45)" strokeWidth="2.5" fill="none" />
          <path id="route2" d="M-20 500 Q160 470 320 450 Q480 430 600 470 Q700 500 740 520" stroke="rgba(196,168,100,0.3)" strokeWidth="2" fill="none" />

          {/* Station dots with pulse */}
          <circle cx="280" cy="400" r="5" fill="rgba(255,255,255,0.6)" className="login-station-dot" />
          <circle cx="480" cy="390" r="4" fill="rgba(255,255,255,0.5)" className="login-station-dot" style={{ animationDelay: "1s" }} />
          <circle cx="560" cy="340" r="5" fill="rgba(255,255,255,0.6)" className="login-station-dot" style={{ animationDelay: "2s" }} />

          {/* Stop marker */}
          <rect x="620" y="330" rx="8" ry="8" width="32" height="32" fill="rgba(196,168,100,0.2)" />
          <rect x="627" y="338" rx="3" ry="3" width="18" height="14" fill="rgba(196,168,100,0.3)" />

          {/* Bus 1 — white circle + small orange bus, upper route */}
          <g className="login-bus">
            <animateMotion dur="18s" repeatCount="indefinite">
              <mpath href="#route1" />
            </animateMotion>
            <circle r="18" fill="#fff" />
            <rect x="-7" y="-9" rx="3" ry="3" width="14" height="16" fill="#E8A020" />
            <rect x="-5" y="-6" rx="1.5" ry="1.5" width="10" height="7" fill="#fff" opacity="0.9" />
            <rect x="-5" y="2.5" rx="0.8" ry="0.8" width="3" height="2.5" fill="#fff" opacity="0.7" />
            <rect x="2" y="2.5" rx="0.8" ry="0.8" width="3" height="2.5" fill="#fff" opacity="0.7" />
            <circle cx="-4" cy="9" r="1.5" fill="#333" />
            <circle cx="4" cy="9" r="1.5" fill="#333" />
          </g>

          {/* Bus 2 — white circle + small orange bus, lower route */}
          <g className="login-bus">
            <animateMotion dur="22s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
              <mpath href="#route2" />
            </animateMotion>
            <circle r="16" fill="#fff" />
            <rect x="-6" y="-8" rx="2.5" ry="2.5" width="12" height="14" fill="#E8A020" />
            <rect x="-4.5" y="-5.5" rx="1.5" ry="1.5" width="9" height="6" fill="#fff" opacity="0.9" />
            <rect x="-4.5" y="2" rx="0.8" ry="0.8" width="2.5" height="2" fill="#fff" opacity="0.7" />
            <rect x="2" y="2" rx="0.8" ry="0.8" width="2.5" height="2" fill="#fff" opacity="0.7" />
            <circle cx="-3" cy="8" r="1.3" fill="#333" />
            <circle cx="3" cy="8" r="1.3" fill="#333" />
          </g>
        </svg>

        <div className="login-brand-body">
          <div className="login-brand-header">
            <div className="login-logo-ring">
              <div className="login-logo-card">
                <img src="/tap-logo-compact.png" alt="TAP" className="login-logo" />
              </div>
            </div>
            <div className="login-org-text">
              <span className="login-org-name">Trade and Projects</span>
              <span className="login-org-sub">ISLAMABAD BRT</span>
            </div>
          </div>

          <ul className="login-features">
            <li>
              <span className="login-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              </span>
              <div>
                <strong>Live fleet map</strong>
                <span>Track every vehicle and its route in real time.</span>
              </div>
            </li>
            <li>
              <span className="login-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </span>
              <div>
                <strong>Dispatch & alerts</strong>
                <span>Message drivers and publish rider-facing notices.</span>
              </div>
            </li>
            <li>
              <span className="login-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </span>
              <div>
                <strong>Timetables & reports</strong>
                <span>Generate schedules and review punctuality.</span>
              </div>
            </li>
          </ul>

          <div className="login-brand-hero">
            <h1 className="login-brand-title">Fleet Operations<br />Control Centre</h1>
            <p className="login-brand-sub">
              Vehicle tracking, dispatch and scheduling for the Islamabad BRT network. Sign in to start your shift.
            </p>
          </div>

          <div className="login-security-notice">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span>Authorised personnel only. Access is logged and monitored. Never share your security PIN.</span>
          </div>

          <div className="login-brand-footer">
            <span>DISPATCHER CONSOLE</span>
            <span className="login-footer-dot">&middot;</span>
            <span>V0.1.0</span>
            <span className="login-footer-dot">&middot;</span>
            <span>SUPPORT EXT. 2400</span>
          </div>
        </div>
      </aside>

      {/* ── Right form pane ── */}
      <main className="login-form-pane">
        <form className="login-form" onSubmit={onSubmit}>
          <h2 className="login-form-title">Welcome back</h2>
          <p className="login-form-desc">Use your agency credentials and security PIN to access the console.</p>

          <div className="login-field">
            <label className="login-label" htmlFor="login-agency">Agency ID</label>
            <div className="login-input-wrap">
              <span className="login-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </span>
              <input
                id="login-agency"
                value={agencyId}
                onChange={(e) => setAgencyId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000..."
                autoComplete="organization"
                required
                disabled={pending}
              />
              {agencySaved && <span className="login-saved-badge">SAVED</span>}
            </div>
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="login-employee">Employee ID</label>
            <div className="login-input-wrap">
              <span className="login-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </span>
              <input
                id="login-employee"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="ADMTEST-OCC"
                autoComplete="username"
                required
                disabled={pending}
              />
            </div>
          </div>

          <div className="login-field">
            <div className="login-label-row">
              <label className="login-label" htmlFor="login-pin">Security PIN</label>
              <button type="button" className="login-forgot-link" tabIndex={-1}>Forgot PIN?</button>
            </div>
            <div className="login-input-wrap">
              <span className="login-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
              <input
                id="login-pin"
                type={pinVisible ? "text" : "password"}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter your PIN"
                autoComplete="current-password"
                required
                disabled={pending}
                aria-invalid={!!error}
                aria-describedby={error ? "login-pin-error" : undefined}
              />
              <button
                type="button"
                className="login-pin-toggle"
                onClick={() => setPinVisible((v) => !v)}
                disabled={pending}
                aria-label={pinVisible ? "Hide PIN" : "Show PIN"}
              >
                {pinVisible ? "Hide" : "Show"}
              </button>
            </div>
            {error && (
              <p id="login-pin-error" className="login-field-error" role="alert">{error}</p>
            )}
          </div>

          <label className="login-remember">
            <span className={`login-toggle ${remember ? "on" : ""}`} role="switch" aria-checked={remember}>
              <span className="login-toggle-thumb" />
            </span>
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="sr-only"
            />
            <span>Keep me signed in on this station</span>
          </label>

          <button className="login-submit" type="submit" disabled={pending}>
            {pending ? "Signing in…" : "Log in to console"}
          </button>

          <div className="login-divider">
            <span>OR</span>
          </div>

          <button type="button" className="login-badge-btn" disabled={pending}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v10"/><path d="M5.6 5.6l4.3 4.3m4.2 4.2l4.3 4.3"/><path d="M1 12h6m6 0h10"/><path d="M5.6 18.4l4.3-4.3m4.2-4.2l4.3-4.3"/></svg>
            Sign in with employee badge
          </button>

          <p className="login-help-text">
            Trouble signing in? <button type="button" className="login-contact-link">Contact your supervisor</button>
          </p>

          <div className="login-form-health">
            <ApiHealthChip />
          </div>
        </form>
      </main>
    </div>
  );
}
